/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/no-unknown-property */
import { useAppNotificationDispatch, useAppSelector } from 'hooks';
import React, { FC, HTMLProps } from 'react';
import { ReactComponent as BellIcon } from 'assets/icons_3/bell.svg';
import { ReactComponent as WarningIcon } from 'assets/icons_3/alert-triangle.svg';
import { ReactComponent as ArrowRight } from 'assets/icons_3/arrows/arrow-right.svg';
import classNames from 'classnames';
import { appNotificationActions } from 'store/app-notifications';
import { useTranslation } from 'react-i18next';
import useAsyncFetch from 'hooks/async-fetch';
import dayjs from 'dayjs';
import { IAppNotification } from 'store/app-notifications/types';
import AppNotificationModal from 'components/AppNotifications/AppNotificationModal/AppNotificationModal';
import OverlayTrigger from 'components/UI_kit_3/Popover/OverlayTrigger/OverlayTrigger';
import NavLink from 'components/SideMenu/NavLink/NavLink';
import Popover from 'components/UI_kit_3/Popover/Popover';
import RouterLinkButton from 'components/UI_kit_3/Button/RouterLinkButton';
import { dateFormatter } from 'utils/formatters';
import styles from './AppNotificationContainer.module.scss';

interface IAppNotificationContainerProps extends HTMLProps<HTMLDivElement> {

}

const AppNotificationContainer: FC<IAppNotificationContainerProps> = ({ className }) => {
  const [t] = useTranslation();
  const { appNotification, user } = useAppSelector((state) => state);
  const thunkNotificationDispatch = useAppNotificationDispatch();
  const timer = React.useRef<number | null>(null);
  const [showNotification, setShowNotification] = React.useState<boolean>(false);
  const [notification, setNotification] = React.useState<IAppNotification | null>(null);

  React.useEffect(() => {
    if (user.twoFaEnforcement?.blocking) {
      return () => {};
    }

    thunkNotificationDispatch(appNotificationActions.fetchNotifications());
    timer.current = window.setInterval(() => {
      thunkNotificationDispatch(appNotificationActions.fetchNotifications());
    }, 30000);

    return () => {
      if (timer.current) {
        clearInterval(timer.current);
      }
    };
  }, []);

  const unreadMessages = React.useMemo(() => appNotification.notifications
    .filter((message) => !message.wasRead)
    .map((message) => message.id), [appNotification]);

  const [handleToggle] = useAsyncFetch({
    async asyncFunc(show: boolean) {
      if (show) {
        thunkNotificationDispatch(appNotificationActions.setRead(unreadMessages));
      } else {
        thunkNotificationDispatch(appNotificationActions.fetchNotifications());
      }
    },
  });

  const showOnLoginMessages = React.useMemo(
    () => appNotification.notifications
      .filter((message) => {
        const now = dayjs();
        const expireDate = dayjs(message.expireDate);
        const nowIsBeforeExpire = message.expireDate === null
        || now.isBefore(expireDate);

        // if (message.showOnPanelLogin && !message.wasRead && nowIsBeforeExpire) {
        //   console.log(message.subjectText);
        // }

        return message.showOnPanelLogin && !message.wasRead && nowIsBeforeExpire;
      }),
    [appNotification.notifications],
  );

  const [handleRead, readLoading] = useAsyncFetch({
    async asyncFunc(id?: number) {
      if (id) {
        await thunkNotificationDispatch(appNotificationActions.setRead([id]));
        await thunkNotificationDispatch(appNotificationActions.fetchNotifications());
      }
    },
  });

  const shownNotifications = React.useMemo(() => appNotification.notifications
    .slice(0, 3), [appNotification.notifications]);

  return (
    <>
      <AppNotificationModal
        key="usual"
        show={showNotification}
        notification={notification}
        size="md"
        onHide={() => setShowNotification(false)}
        onExited={() => setNotification(null)}
      />
      <AppNotificationModal
        key="important"
        show={showOnLoginMessages.length > 0 && !readLoading}
        onHide={() => handleRead(showOnLoginMessages[0]?.id)}
        notification={showOnLoginMessages[0]}
      />
      <OverlayTrigger
        position="bottom-right"
        className={styles.listWrapper}
        overlay={(
          <Popover className={styles.list} qa-element="app-notifications-list">
            <div className={styles.header}>
              <h6>{t('app_notification.list')}</h6>
              <RouterLinkButton variant="link" className={styles.showAll} suffixIcon={<ArrowRight />} to="/settings/notifications-list">{t('app_notification.show_all')}</RouterLinkButton>
            </div>
            <div className={styles.body}>
              {shownNotifications.map((_notification) => (
                <div
                  key={_notification.id}
                  className={classNames(styles.notification, {
                    [styles.wasRead]: notification?.wasRead,
                  })}
                >
                  <div className={styles.notificationHeader}>
                    {_notification.important ? <WarningIcon />
                      : <BellIcon className={styles.bellIcon} />}
                    <span>{dateFormatter(_notification.dispatchDate, 'DD.MM.YYYY HH:mm')}</span>
                  </div>
                  <button onClick={() => { setNotification(_notification); setShowNotification(true); }} qa-element="show-notification" type="button" className={styles.notificationBody}>
                    {_notification.subjectText}
                  </button>
                </div>
              ))}
            </div>
          </Popover>
      )}
      >
        <NavLink Icon={BellIcon} className="mr-2xs" qa-element="app-notifications-toggle" />
      </OverlayTrigger>
    </>
  );
};

export default AppNotificationContainer;
